/**
 * Navigation model — the single place that knows which modules exist, which tabs
 * each one has, and in what order.
 *
 * Sources, in precedence order:
 *  - shot `2c` — the FinOps tab bar, left to right
 *  - shot `2n` — the Administração tab bar, and its right-hand action button
 *  - shot `3a`/`3b`/`3c` (handoff v4) — Compliance: Perguntas do padrão · Questionário · Documentos
 *  - shot `2a` — the navigation map, which fixes the module icons
 *
 * `permissao` is the key the resolver of story 0.4 will look up per tela/aba, and
 * `flag` the one story 0.4's `useFlag` will read. Both are declared now so the
 * screens never have to invent a key later; neither is enforced yet.
 */

export interface Aba {
  /** UI copy, pt-BR, sentence case as drawn in the shots. */
  rotulo: string
  /** Path segment relative to the module base. Empty string = index route. */
  segmento: string
  /** Handoff shot id, so a screen and its spec never drift apart. */
  shot?: string
  /** Permission key — `Map<telaOuAba, {ver, editar}>` in story 0.4. */
  permissao: string
  /** Phosphor icon drawn before the label — the lock on Compliance's IOX-only tab (`3a`). */
  icone?: string
  /**
   * True for a tab that lives OUTSIDE the client scope although it sits in a
   * client-scoped module's tab bar — `Perguntas do padrão` (`/compliance/perguntas`,
   * BR-23). Its link is absolute, not `${base}/${segmento}`.
   */
  global?: boolean
}

export interface Modulo {
  id: 'finops' | 'orcamento' | 'compliance' | 'admin'
  rotulo: string
  /** Phosphor regular icon name, per shot `2a`. */
  icone: string
  /** True for modules scoped to a client (BR-19); false for Administração. */
  escopadoPorCliente: boolean
  abas: Aba[]
  permissao: string
  flag: string
}

export const MODULOS: Modulo[] = [
  {
    id: 'finops',
    rotulo: 'FinOps',
    icone: 'chart-line-up',
    escopadoPorCliente: true,
    permissao: 'finops',
    flag: 'modulo.finops',
    abas: [
      // Settled 2026-08-12: the first tab is the dashboard publicado (`2g`).
      // There is no CUDOS dashboard to mirror and none to build.
      { rotulo: 'Dashboard', segmento: 'dashboard', shot: '2g', permissao: 'finops.dashboard' },
      { rotulo: 'Cost Explorer', segmento: 'custos', shot: '2c', permissao: 'finops.custos' },
      { rotulo: 'Discovery', segmento: 'discovery', shot: '2d', permissao: 'finops.discovery' },
      {
        rotulo: 'Oportunidades',
        segmento: 'oportunidades',
        shot: '2e',
        permissao: 'finops.oportunidades',
      },
      {
        rotulo: 'Configurações',
        segmento: 'configuracoes',
        shot: '2b',
        permissao: 'finops.configuracoes',
      },
    ],
  },
  {
    id: 'orcamento',
    rotulo: 'Orçamento',
    icone: 'currency-dollar',
    escopadoPorCliente: true,
    permissao: 'orcamento',
    flag: 'modulo.orcamento',
    abas: [
      { rotulo: 'Dashboard', segmento: 'dashboard', shot: '2i', permissao: 'orcamento.dashboard' },
      {
        rotulo: 'Contratos & aditivos',
        segmento: 'contratos',
        shot: '2j',
        permissao: 'orcamento.contratos',
      },
      {
        rotulo: 'Faturas & lançamentos',
        segmento: 'faturas',
        shot: '2k',
        permissao: 'orcamento.faturas',
      },
    ],
  },
  {
    id: 'compliance',
    rotulo: 'Compliance',
    icone: 'shield-check',
    escopadoPorCliente: true,
    permissao: 'compliance',
    flag: 'modulo.compliance',
    // Handoff v4 (16/09): three tabs. `Perguntas do padrão` (`3a`) is GLOBAL and
    // IOX-only (BR-23 — Administrador + Arquiteto): it is routed outside the client
    // scope and the frame renders it only when `compliance.perguntas` is `ver`. The
    // Cliente sees two tabs, never a disabled third one.
    abas: [
      {
        rotulo: 'Perguntas do padrão',
        segmento: 'perguntas',
        shot: '3a',
        permissao: 'compliance.perguntas',
        icone: 'lock',
        global: true,
      },
      {
        rotulo: 'Questionário',
        segmento: 'questionario',
        shot: '3b',
        permissao: 'compliance.questionario',
      },
      // Sempre disponível (Fábio, 23/10/2026) — o handoff só a abria no "primeiro acesso".
      {
        rotulo: 'Sugestões de evidências',
        segmento: 'novo-padrao',
        shot: '3d',
        permissao: 'compliance.questionario',
      },
      { rotulo: 'Documentos', segmento: 'documentos', shot: '3c', permissao: 'compliance.documentos' },
    ],
  },
  {
    id: 'admin',
    rotulo: 'Administração',
    icone: 'gear-six',
    escopadoPorCliente: false,
    permissao: 'admin',
    flag: 'modulo.admin',
    abas: [
      { rotulo: 'Clientes', segmento: 'clientes', shot: '2n', permissao: 'admin.clientes' },
      {
        rotulo: 'Usuários & convites',
        segmento: 'usuarios',
        shot: '2o',
        permissao: 'admin.usuarios',
      },
      {
        rotulo: 'Perfis & permissões',
        segmento: 'perfis',
        shot: '2q',
        permissao: 'admin.perfis',
      },
      {
        rotulo: 'Configurações globais',
        segmento: 'configuracoes',
        shot: '2p',
        permissao: 'admin.configuracoes',
      },
      {
        // Fase 4 do roadmap de IA (`V0021`) — sem shot/wireframe: a tela não
        // existia quando os handoffs foram desenhados. Aba PRÓPRIA, e não um
        // card dentro de Configurações globais: o modelo de permissão deste
        // projeto é 1 aba = 1 rota = 1 permissão (ver `Perfis.tsx`), e colar
        // aqui dentro de `admin.configuracoes` esconderia esta permissão da
        // árvore de `2q`.
        rotulo: 'Materiais por especialista',
        segmento: 'materiais',
        permissao: 'admin.materiais_ia',
      },
    ],
  },
]

/**
 * Base path of a module for a given client. Administração is never client-scoped.
 *
 * 🔒 `idCliente` accepts `null` and the return is `string | null`, so a caller with
 * no client in hand has to DEAL with it. It used to take a plain `string`, and the
 * sidebar passed `idCliente ?? ''` — which built `/cliente//finops`, normalised by
 * the router to `/cliente/finops`, and the tenant guard then reported "the client
 * `finops` is not in your list". A nullable signature turns that silent breakage
 * into a compile error.
 */
export function basePath(modulo: Modulo, idCliente: string | null): string | null {
  if (!modulo.escopadoPorCliente) return `/${modulo.id}`
  return idCliente ? `/cliente/${idCliente}/${modulo.id}` : null
}
